/**
 * La barra che compare con "Personalizza": aggiungi, ripristina, annulla,
 * salva. E' una pillola scura sospesa in alto, che resta visibile mentre si
 * scorre. Niente si salva da solo: "Annulla" con modifiche aperte chiede
 * conferma.
 */

import { useState } from 'react';
import { Check, Loader2, Plus, RotateCcw, Sparkles } from 'lucide-react';
import { cn } from '../../lib/cn';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

interface Props {
  modificato: boolean;
  inSalvataggio: boolean;
  onAggiungi: () => void;
  onRipristina: () => void;
  onAnnulla: () => void;
  onSalva: () => void;
}

const PULSANTE = 'inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-sm font-semibold transition disabled:opacity-50';

export function BarraModifica({ modificato, inSalvataggio, onAggiungi, onRipristina, onAnnulla, onSalva }: Props) {
  const [confermaAnnulla, setConfermaAnnulla] = useState(false);

  return (
    <div className="sticky top-2 z-40 flex flex-col gap-3 rounded-[1.75rem] bg-neutral-900/90 px-4 py-3 text-white shadow-2xl ring-1 ring-white/10 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between">
      <p className="flex items-center gap-2 text-sm">
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 to-primary-700">
          <Sparkles className="h-4 w-4" />
        </span>
        <span>
          <span className="font-display font-semibold">Personalizza la tua dashboard</span>
          <span className="hidden text-white/60 2xl:inline"> · trascina i widget, cambia taglia con le formine, togli col pallino</span>
        </span>
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" onClick={onAggiungi} className={cn(PULSANTE, 'bg-white text-neutral-900 hover:bg-white/90')}>
          <Plus className="h-4 w-4" /> Aggiungi widget
        </button>
        <button type="button" onClick={onRipristina} title="Torna alla dashboard di partenza del tuo ruolo" className={cn(PULSANTE, 'text-white/80 hover:bg-white/10 hover:text-white')}>
          <RotateCcw className="h-4 w-4" /> Ripristina
        </button>
        <button type="button" onClick={() => (modificato ? setConfermaAnnulla(true) : onAnnulla())} disabled={inSalvataggio} className={cn(PULSANTE, 'text-white/80 hover:bg-white/10 hover:text-white')}>
          Annulla
        </button>
        <button type="button" onClick={onSalva} disabled={inSalvataggio} className={cn(PULSANTE, 'bg-primary-600 px-4 text-white shadow-lg shadow-primary-900/40 hover:bg-primary-500')}>
          {inSalvataggio ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Salva
        </button>
      </div>

      <AlertDialog open={confermaAnnulla} onOpenChange={setConfermaAnnulla}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lasciare le modifiche?</AlertDialogTitle>
            <AlertDialogDescription>
              La dashboard torna com&apos;era prima di Personalizza. Le modifiche fatte finora non vengono salvate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continua a modificare</AlertDialogCancel>
            <AlertDialogAction onClick={onAnnulla}>Lascia le modifiche</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
